function rankCartRecommendations(entries, cartProductIds, limit) {
  const cartIds = new Set(cartProductIds.map(String));
  const candidates = new Map();

  entries.forEach((entry) => {
    const productId = String(entry.productId);
    if (!productId || cartIds.has(productId)) return;

    const current = candidates.get(productId) || {
      productId,
      element: entry.element,
      anchors: new Set(),
      bestRank: Number.POSITIVE_INFINITY,
      totalRank: 0,
      firstAnchor: Number.POSITIVE_INFINITY,
    };

    if (!current.anchors.has(entry.anchorIndex)) {
      current.anchors.add(entry.anchorIndex);
      current.totalRank += entry.rank;
    }
    current.bestRank = Math.min(current.bestRank, entry.rank);
    current.firstAnchor = Math.min(current.firstAnchor, entry.anchorIndex);
    candidates.set(productId, current);
  });

  return [...candidates.values()]
    .sort((a, b) =>
      b.anchors.size - a.anchors.size ||
      a.bestRank - b.bestRank ||
      a.totalRank - b.totalRank ||
      a.firstAnchor - b.firstAnchor
    )
    .slice(0, limit);
}

if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.loadRecommendations();
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshSection());
        }
        this.addEventListener('submit', this.trackAddToCart.bind(this));
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      async loadRecommendations() {
        const productIds = this.dataset.productIds.split(',').filter(Boolean);
        const cartProductIds = this.dataset.cartProductIds.split(',').filter(Boolean);
        const limit = Number(this.dataset.limit) || 3;
        const results = this.querySelector('[data-recommendation-results]');
        const shell = this.querySelector('[data-recommendation-shell]');
        const fallbackTemplate = this.querySelector('template[data-fallback-candidates]');
        if (!productIds.length || !results || !shell) return;

        try {
          const responses = await Promise.all(
            productIds.map(async (productId, anchorIndex) => {
              const url = `${this.dataset.url}&section_id=${encodeURIComponent(this.dataset.sectionId)}&product_id=${encodeURIComponent(productId)}`;
              const response = await fetch(url);
              if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              return [...html.querySelectorAll('[data-recommendation-card]')].map((element) => ({
                productId: element.dataset.productId,
                rank: Number(element.dataset.rank) || 0,
                anchorIndex,
                element,
              }));
            })
          );

          let ranked = rankCartRecommendations(responses.flat(), cartProductIds, limit);
          if (!ranked.length && fallbackTemplate) {
            ranked = [...fallbackTemplate.content.querySelectorAll('[data-fallback-card]')]
              .filter((element) => !cartProductIds.includes(element.dataset.productId))
              .slice(0, limit)
              .map((element) => ({ element }));
          }
          results.replaceChildren(...ranked.map((candidate) => candidate.element));

          if (ranked.length) {
            shell.hidden = false;
            const status = this.querySelector('[data-recommendation-status]');
            if (status) status.textContent = `${ranked.length} recommendations loaded.`;
          }
        } catch (error) {
          console.error('Cart recommendations could not be loaded.', error);
        } finally {
          this.setAttribute('aria-busy', 'false');
        }
      }

      async refreshSection() {
        try {
          const response = await fetch(`${window.routes.cart_url}?section_id=${encodeURIComponent(this.dataset.sectionId)}`);
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const updated = html.querySelector('cart-recommendations');
          if (updated) this.replaceWith(updated);
          else this.remove();
        } catch (error) {
          console.error('Cart recommendations could not be refreshed.', error);
        }
      }

      trackAddToCart(event) {
        if (!event.target.matches('[data-cart-recommendation-form]')) return;
        const card = event.target.closest('[data-recommendation-card], [data-fallback-card]');
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'cart_recommendation_add', {
            product_id: card?.dataset.productId || 'unknown',
            recommendation_strategy: card?.dataset.strategy || 'unknown',
            source_surface: 'cart_recommendations',
          });
        }
      }
    }
  );
}
